"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import React, { type ReactNode } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import Link from "@/lib/locale/Link";

export type ForgotPasswordFormData = { email: string };

type ForgotPasswordFormFieldsProps = {
  defaultValues?: ForgotPasswordFormData;
  isLoading?: boolean;
  onSubmit: SubmitHandler<ForgotPasswordFormData>;
  turnstileToken: string | null;
  turnstileVerification: ReactNode;
};

export const ForgotPasswordFormFields = ({
  defaultValues = { email: "" },
  isLoading = false,
  onSubmit,
  turnstileToken,
  turnstileVerification,
}: ForgotPasswordFormFieldsProps) => {
  const t = useTranslations();
  const formSchema = z.object({
    email: z.string().email({ message: t("auth.emailInputError") }),
  });
  const form = useForm<ForgotPasswordFormData>({
    defaultValues,
    resolver: zodResolver(formSchema),
  });

  return (
    <div className="w-full max-w-sm rounded-lg border bg-card p-8">
      <h1 className="text-center text-xl font-semibold">{t("auth.forgotPasswordHeader")}</h1>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("auth.emailInputLabel")}</FormLabel>
                <FormControl>
                  <Input
                    autoComplete="email"
                    spellCheck={false}
                    type="email"
                    placeholder={t("auth.emailInputPlaceholder")}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {turnstileVerification}
          <Button
            aria-busy={isLoading}
            loading={isLoading}
            disabled={isLoading || !turnstileToken}
            type="submit"
            className="w-full"
          >
            {t("auth.forgotPasswordSubmitButton")}
          </Button>
        </form>
      </Form>
      <div className="mt-8 space-y-4 text-center text-foreground/80 text-sm">
        <div>
          {t.rich("auth.alreadyRegisteredLogin", {
            prefix: (chunks) => <span>{chunks}</span>,
            loginLink: (chunks) => (
              <Link href="/app/auth/sign-in" className="underline">
                {chunks}
              </Link>
            ),
          })}
        </div>
        <div>
          {t.rich("auth.noUserRegister", {
            prefix: (chunks) => <span>{chunks}</span>,
            registerLink: (chunks) => (
              <Link href="/app/auth/sign-up" className="underline">
                {chunks}
              </Link>
            ),
          })}
        </div>
      </div>
    </div>
  );
};
